import { utils } from '@digital-commerce/shared-utils'
import {
  allPass,
  complement,
  evolve,
  filter,
  includes,
  join,
  map,
  path,
  pipe,
  prop,
  propOr,
  isEmpty,
  memoizeWith,
  isNil
} from 'ramda'
import { formatCurrency } from '../../../utils/currency'
import { buildHSCreditList } from '../../../utils/display'
import {
  buildHSGroupedRecurringDiscountList,
  buildHSNonRecurringDiscounts
} from '../../../utils/discount'
import { getCacheKey } from '../../../utils/cart'
import {
  formatHSEppDiscount,
  getHSEppDiscount,
  getServiceWithoutSpecifiedDiscounts
} from '../../../utils/homeSolutions'

const { getHSService, getDisplayName, getMonthlyItemPrice } = utils.cart

const AUTH_CHANGE_URL = '/my-account/manage-services/home/home-phone/plans'
const UNAUTH_CHANGE_URL = '/shop/home-services/bundle/build-your-own'

const getCallingFeatures = pipe(
  propOr([], 'items'),
  filter(pipe(prop('tags'), includes('feature')))
)

const getHomePhoneAddons = pipe(propOr([], 'items'), filter(pipe(prop('tags'), includes('addon'))))

const getAdditionalFees = pipe(
  propOr([], 'items'),
  filter(pipe(prop('tags'), includes('homephone-fee')))
)

const buildSubItemsList = (subItems, lang) => {
  const mapper = (featureItem) => {
    const { id } = featureItem
    const name = getDisplayName(featureItem)
    const price = `${formatCurrency(getMonthlyItemPrice(featureItem).subTotal, lang)}/mo.`

    return {
      id,
      name,
      price
    }
  }

  return subItems?.map(mapper)
}

export const getHomePhoneService = memoizeWith(getCacheKey('HS-homephone'), (cart) => {
  if (!cart?.root?.items) return undefined
  return pipe(
    getHSService(['homephone']),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

export const getChangeUrlHomePhone = (isAuthenticated) => {
  if (isAuthenticated) {
    return AUTH_CHANGE_URL
  }
  return UNAUTH_CHANGE_URL
}

export const getLongDistanceAddonDisplayNames = pipe(
  propOr([], 'items'),
  filter(
    pipe(
      prop('tags'),
      allPass([
        includes('addon'),
        complement(includes('pay-per-minute')),
        includes('long-distance')
      ])
    )
  ),
  map(getDisplayName),
  join(', ')
)

export const buildCallingFeaturesList = (homePhone, lang) => {
  return buildSubItemsList(getCallingFeatures(homePhone), lang)
}

export const buildHomePhoneAddonsList = (homePhone, lang) => {
  return buildSubItemsList(getHomePhoneAddons(homePhone), lang)
}

export const buildAdditionalFeesList = (homePhone, lang) => {
  return buildSubItemsList(getAdditionalFees(homePhone), lang)
}

export const buildRecurringDiscountList = (homePhone, lang, translate, isAffiliates) => {
  // Business rule: EPP discount must be displayed as a separate line item, and is displayed last
  const eppDiscount = getHSEppDiscount(homePhone)
  if (eppDiscount) {
    const homePhoneWithoutEppDiscount = getServiceWithoutSpecifiedDiscounts(homePhone, [
      eppDiscount
    ])
    return [
      ...buildHSGroupedRecurringDiscountList(homePhoneWithoutEppDiscount, lang, translate),
      formatHSEppDiscount(eppDiscount, translate, lang, isAffiliates)
    ]
  }

  return buildHSGroupedRecurringDiscountList(homePhone, lang, translate)
}

export const buildDiscountsAndCreditsList = (homePhone, cart, lang) => {
  const credits = buildHSCreditList('homephone', cart, lang)
  const discounts = buildHSNonRecurringDiscounts([homePhone], lang)
  return [...discounts, ...credits]
}

export const getAttributesToAppend = (homePhone, translate) => {
  const appendAttributes = []
  const longDistanceAddons = getLongDistanceAddonDisplayNames(homePhone)
  if (!isEmpty(longDistanceAddons)) appendAttributes.push(longDistanceAddons)
  appendAttributes.push(translate('item.proInstall'))

  return appendAttributes
}

export const shouldRender = (cart) => {
  const service = getHomePhoneService(cart)
  return !isNil(service) && !isEmpty(service)
}
